// src/pages/ExportPay.js

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Nav } from "../../components/nav";
import { useAuth } from "../../navigation/AuthProvider";
import "../css/ExportPay.css";
import axios from "axios";
import { BACKEND_URL } from "../../constants";
import { Toast } from "../../components/ToastPopup";
import { calculatePayForEmployee } from "../../utils/calculatePayForEmployee";
import FortnoxSetupWizard from "../../components/Fortnox/FortnoxSetupWizard";
import VismaSetupWizard from "../../components/visma/VismaSetupWizard";
import ExportSystemSelectModal from "../../components/ExportSystemSelectModal";

import { getPermission } from "../../permissions/rolePermissions";


const ExportPay = () => {
  const {
    employees,
    draftSchedule,
    setDraftSchedule,
    company,
    user,
    pays,
    vacationPay,
    overtimeRules,
    obs,
    absenceRules,
    timeSchemas,
  } = useAuth();

  const employeesPermission = getPermission(user.role, "payroll:export");
  const canEditEmployees = employeesPermission === "edit";



  const [searchQuery, setSearchQuery] = useState("");

  // 🔹 Läs vald månad/år från localStorage om det finns, annars nuvarande
  const [selectedMonth, setSelectedMonth] = useState(() => {
    try {
      const stored = window.localStorage.getItem("ts_exportPay_selectedMonth");
      const n = stored ? parseInt(stored, 10) : NaN;
      if (Number.isFinite(n) && n >= 1 && n <= 12) return n;
    } catch (_) {}
    return new Date().getMonth() + 1;
  });

  const [selectedYear, setSelectedYear] = useState(() => {
    try {
      const stored = window.localStorage.getItem("ts_exportPay_selectedYear");
      const n = stored ? parseInt(stored, 10) : NaN;
      if (Number.isFinite(n)) return n;
    } catch (_) {}
    return new Date().getFullYear();
  });

  const [selectedTimeSchema, setSelectedTimeSchema] = useState("");
  const [loading, setLoading] = useState(false);
  const isFetchingRef = useRef(false);

  // ✅ Håll koll på vilka {år, månad} vi redan försökt hämta – även om resultatet blev tomt
  const fetchedMonthsRef = useRef(new Set());

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [exporting, setExporting] = useState(false);


  const [showExportSystemSelect, setShowExportSystemSelect] = useState(false);
  const [selectedExportSystem, setSelectedExportSystem] = useState(null);

  // ✅ PAXml/Wizard
  const [showFortnoxWizard, setShowFortnoxWizard] = useState(false);
  const [showVismaWizard, setShowVismaWizard] = useState(false);

  const monthsInSwedish = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  // ✅ Samma helper som Payroll/Employees
  const isShiftCompleteForPayroll = (shift) => {
    return !!(
      shift &&
      shift.status === "complete" &&
      shift.date &&
      typeof shift.startTime === "string" &&
      shift.startTime.includes(":") &&
      typeof shift.endTime === "string" &&
      shift.endTime.includes(":") &&
      Array.isArray(shift.employees) &&
      shift.project &&
      shift.employees.some((e) => !!e?.employeeId)
    );
  };

  // 🔁 Hämta månadens skift om de inte finns i draftSchedule
  useEffect(() => {
    const monthKey = `${selectedYear}-${selectedMonth}`;

    // ⛔️ Redan hämtad (även om resultatet blev tomt) → gör inget
    if (fetchedMonthsRef.current.has(monthKey)) {
      return;
    }

    const hasMonthData = draftSchedule.some((shift) => {
      const d = new Date(shift.date);
      return d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear;
    });

    // Om vi redan har skift i denna månad/år, markera som hämtad och avbryt
    if (hasMonthData) {
      fetchedMonthsRef.current.add(monthKey);
      return;
    }

    const fetchShifts = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/get-shifts-for-month`, {
          params: {
            userId: user._id,
            companyId: company._id,
            selectedMonth,
            selectedYear,
          },
        });

        setDraftSchedule((prev) => {
          const filteredPrev = prev.filter((s) => {
            const d = new Date(s.date);
            return !(d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear);
          });
          return [...filteredPrev, ...(data.shifts || [])];
        });

        // ✅ Viktigt: markera denna månad som hämtad även om data.shifts är tomt
        fetchedMonthsRef.current.add(monthKey);
      } catch (err) {
        console.error("Failed to fetch shifts:", err);
        setToastType("error");
        setToastMessage("Kunde inte hämta skift för vald period. Försök igen.");
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchShifts();
  }, [selectedMonth, selectedYear, user._id, company._id, setDraftSchedule, draftSchedule]);

  // ✅ Matcha Payroll/Employees: om schema är valt, ta bara anställda som faktiskt har skift i det schemat
  const employeesWithShiftsInSchema = useMemo(() => {
    if (!selectedTimeSchema) return employees || [];

    return (employees || []).filter((emp) =>
      draftSchedule.some((shift) => {
        if (!isShiftCompleteForPayroll(shift)) return false;
        const d = new Date(shift.date);
        const isCorrectMonth = d.getMonth() + 1 === selectedMonth;
        const isCorrectYear = d.getFullYear() === selectedYear;
        const matchesSchema = shift.timeSchema === selectedTimeSchema;
        const hasEmployee = shift.employees.some((e) => e.employeeId === emp._id);
        return isCorrectMonth && isCorrectYear && matchesSchema && hasEmployee;
      })
    );
  }, [employees, draftSchedule, selectedTimeSchema, selectedMonth, selectedYear]);

  // ✅ Sökning över samma lista
  const filteredEmployees = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    const baseList = employeesWithShiftsInSchema;

    return (baseList || []).filter((emp) => {
      return (
        (emp.name || "").toLowerCase().includes(q) ||
        (emp.email || "").toLowerCase().includes(q) ||
        (emp.role || "").toLowerCase().includes(q)
      );
    });
  }, [employeesWithShiftsInSchema, searchQuery]);

  // ✅ Samma filtrering av skift per anställd som i Payroll
  const getShiftsForEmployeeThisMonth = (employeeId) => {
    return draftSchedule.filter((shift) => {
      if (!isShiftCompleteForPayroll(shift)) return false;
      const d = new Date(shift.date);
      const matchesMonth = d.getMonth() + 1 === selectedMonth;
      const matchesYear = d.getFullYear() === selectedYear;
      const matchesSchema = !selectedTimeSchema || shift.timeSchema === selectedTimeSchema;
      const matchesEmployee = shift.employees.some((e) => e.employeeId === employeeId);
      return matchesMonth && matchesYear && matchesSchema && matchesEmployee;
    });
  };

  // ✅ Bygg tabell-data från calculatePayForEmployee (returnDetailed)
  const tableData = useMemo(() => {
    return filteredEmployees.map((emp) => {
      const empShifts = getShiftsForEmployeeThisMonth(emp._id);

      const payResult = calculatePayForEmployee({
        employee: emp,
        shifts: empShifts,
        payRates: pays || [],
        vacationPay: vacationPay || {},
        overtimeRules: overtimeRules || [],
        obRules: obs || [],
        absenceRules: absenceRules || [],
        returnDetailed: true,
      });

      const {
        summary = {},
        overtimeDetails = [],
        absenceDetails = [],
        // OBS: om du senare lägger till obDetails i calculatePayForEmployee så tar vi med det här automatiskt
        obDetails = [],
      } = payResult || {};

      const {
        totalHours = 0,
        baseHours = 0,
        overtimeHours = 0,
        absenceHours = 0,
        totalPay = 0,
        obPay = 0,
        overTimePay = 0,
        vacationCompensation = 0,
      } = summary || {};

      return {
        emp,
        totalHours,
        baseHours,
        overtimeHours,
        absenceHours,
        totalPay,
        obPay,
        overTimePay,
        vacationCompensation,

        // 👇 skickas vidare till wizard → export
        overtimeDetails: Array.isArray(overtimeDetails) ? overtimeDetails : [],
        absenceDetails: Array.isArray(absenceDetails) ? absenceDetails : [],
        obDetails: Array.isArray(obDetails) ? obDetails : [],
      };
    });
  }, [
    filteredEmployees,
    draftSchedule,
    selectedMonth,
    selectedYear,
    selectedTimeSchema,
    pays,
    vacationPay,
    overtimeRules,
    obs,
    absenceRules,
  ]);

  const totals = useMemo(() => {
    const employeesCount = tableData.length;
    const totalHours = tableData.reduce((sum, r) => sum + (Number(r.totalHours) || 0), 0);
    const totalPay = tableData.reduce((sum, r) => sum + (Number(r.totalPay) || 0), 0);
    return { employeesCount, totalHours, totalPay };
  }, [tableData]);

  // 🔹 LÖNEPERIOD (utbetalningslön): nästa månad
  const payoutPeriod = useMemo(() => {
    let payoutMonth = selectedMonth + 1;
    let payoutYear = selectedYear;
    if (payoutMonth === 13) {
      payoutMonth = 1;
      payoutYear = selectedYear + 1;
    }
    return { payoutMonth, payoutYear };
  }, [selectedMonth, selectedYear]);

  // 🔹 Export payload till wizard (PAXml underlag)
  const exportPayload = useMemo(() => {
    if (!tableData || tableData.length === 0) return null;

    const transactions = tableData.map(
      ({
        emp,
        baseHours,
        overtimeHours,
        absenceHours,
        totalHours,
        overTimePay,
        obPay,
        vacationCompensation,
        totalPay,
        overtimeDetails,
        absenceDetails,
        obDetails,
      }) => ({
        // ✅ Viktigt: employeeId = TS User._id (samma som du använder i skift.employees[].employeeId)
        employeeId: emp._id,

        baseHours: Number(baseHours || 0),
        overtimeHours: Number(overtimeHours || 0),
        absenceHours: Number(absenceHours || 0),
        totalHours: Number(totalHours || 0),

        // pengar kan fortfarande användas i UI, men PAXml exporten ska primärt bygga på timmar + koder
        overTimePay: Number(overTimePay || 0),
        obPay: Number(obPay || 0),
        vacationCompensation: Number(vacationCompensation || 0),
        totalPay: Number(totalPay || 0),

        overtimeDetails: Array.isArray(overtimeDetails) ? overtimeDetails : [],
        absenceDetails: Array.isArray(absenceDetails) ? absenceDetails : [],
        obDetails: Array.isArray(obDetails) ? obDetails : [],
      })
    );

    const workLabel = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;
    const payoutLabel = `${payoutPeriod.payoutYear}-${String(payoutPeriod.payoutMonth).padStart(2, "0")}`;

    return {
      transactions,
      meta: {
        // Arbetad period (underlag)
        workYear: selectedYear,
        workMonth: selectedMonth,

        // Löneperiod (utbetalning) - fortfarande bra att ha i metadata/filnamn
        year: payoutPeriod.payoutYear,
        month: payoutPeriod.payoutMonth,

        timeSchemaId: selectedTimeSchema || null,

        periodLabel: `Tidrapport ${workLabel} (utbetalning ${payoutLabel})`,
        source: "TimeSmart ExportPay",
      },
    };
  }, [tableData, selectedYear, selectedMonth, selectedTimeSchema, payoutPeriod]);

  const formatHours = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0,00";
    return n.toFixed(2).replace(".", ",");
  };

  const formatCurrency = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0 kr";
    return (
      n.toLocaleString("sv-SE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }) + " kr"
    );
  };

  const canOpenWizard = !loading && !exporting && (tableData?.length || 0) > 0;

  return (
    <div className="export-pay-page">
      <Nav />
      <div className="export-pay-container">
        <div className="export-pay-header-row">
          <div>
            <h1 className="export-pay-title">Löneexport & sammanställning</h1>
            <p className="export-pay-subtitle">
              Översikt över arbetade timmar och lön för vald period. Perfekt som underlag inför export till lönesystem.
            </p>
          </div>

          {canEditEmployees  && (
            <button
              className="export-pay-button"
              onClick={() => setShowExportSystemSelect(true)}
              disabled={!canOpenWizard}
            >
              Exportera löneunderlag
            </button>
          )}
        </div>

        <div className="export-pay-summary">
          <div className="export-pay-summary-header">
            <span>
              Sammanfattning – {monthsInSwedish[selectedMonth - 1]} {selectedYear}
            </span>
            <p className="export-pay-summary-sub">
              Arbetad period:{" "}
              <strong>
                {monthsInSwedish[selectedMonth - 1]} {selectedYear}
              </strong>{" "}
              · Löneperiod (utbetalning):{" "}
              <strong>
                {monthsInSwedish[payoutPeriod.payoutMonth - 1]} {payoutPeriod.payoutYear}
              </strong>
            </p>
          </div>

          <div className="export-pay-summary-cards">
            <div className="export-pay-summary-card">
              <div className="summary-label">Antal anställda</div>
              <div className="summary-value">{totals.employeesCount}</div>
            </div>

            <div className="export-pay-summary-card">
              <div className="summary-label">Totalt arbetade timmar</div>
              <div className="summary-value">{formatHours(totals.totalHours)} h</div>
            </div>

            <div className="export-pay-summary-card">
              <div className="summary-label">Total lön (brutto)</div>
              <div className="summary-value">{formatCurrency(totals.totalPay)}</div>
            </div>
          </div>
        </div>

        <div className="export-pay-filters">
          <div className="search-bar-container">
            <input
              type="text"
              className="search-input"
              placeholder="Sök efter namn, email eller roll..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            className="export-pay-select"
            value={selectedTimeSchema}
            onChange={(e) => setSelectedTimeSchema(e.target.value)}
          >
            <option value="">Alla scheman</option>
            {(timeSchemas || []).map((ts) => (
              <option key={ts._id} value={ts._id}>
                {ts.name}
              </option>
            ))}
          </select>

          <select
            className="export-pay-select"
            value={selectedMonth}
            onChange={(e) => {
              const m = parseInt(e.target.value, 10);
              setSelectedMonth(m);
              try {
                window.localStorage.setItem("ts_exportPay_selectedMonth", String(m));
              } catch (_) {}
            }}
          >
            {monthsInSwedish.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>

          <select
            className="export-pay-select"
            value={selectedYear}
            onChange={(e) => {
              const y = parseInt(e.target.value, 10);
              setSelectedYear(y);
              try {
                window.localStorage.setItem("ts_exportPay_selectedYear", String(y));
              } catch (_) {}
            }}
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + 1 - i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className="export-pay-table-wrapper">
          {loading ? (
            <div className="spinner-container">
              <div className="spinner" />
            </div>
          ) : tableData.length === 0 ? (
            <div className="export-pay-empty">Inga anställda eller skift hittades för vald period.</div>
          ) : (
            <table className="export-pay-table">
              <thead>
                <tr>
                  <th>Namn</th>
                  <th className="numeric-cell">Ordinarie timmar</th>
                  <th className="numeric-cell">Övertid timmar</th>
                  <th className="numeric-cell">Frånvaro timmar</th>
                  <th className="numeric-cell">Totalt timmar</th>
                  <th className="numeric-cell">ÖT-pengar</th>
                  <th className="numeric-cell">OB-pengar</th>
                  <th className="numeric-cell">Semesterersättning</th>
                  <th className="numeric-cell">Total lön</th>
                </tr>
              </thead>

              <tbody>
                {tableData.map(
                  (
                    {
                      emp,
                      baseHours,
                      overtimeHours,
                      absenceHours,
                      totalHours,
                      overTimePay,
                      obPay,
                      vacationCompensation,
                      totalPay,
                    },
                    idx
                  ) => (
                    <tr key={emp._id || idx}>
                      <td>
                        <div className="emp-name">{emp.name || "Okänd"}</div>
                        {emp.email && <div className="emp-sub">{emp.email}</div>}
                        {(emp.role || emp.title) && (
                          <div className="emp-sub-muted">{emp.role || emp.title}</div>
                        )}
                      </td>

                      <td className="numeric-cell" style={{ paddingLeft: 13 }}>
                        {formatHours(baseHours)}
                      </td>
                      <td className="numeric-cell" style={{ paddingLeft: 13 }}>
                        {formatHours(overtimeHours)}
                      </td>
                      <td className="numeric-cell" style={{ paddingLeft: 13 }}>
                        {formatHours(absenceHours)}
                      </td>
                      <td className="numeric-cell" style={{ paddingLeft: 13 }}>
                        {formatHours(totalHours)}
                      </td>
                      <td className="numeric-cell" style={{ paddingLeft: 13 }}>
                        {formatCurrency(overTimePay)}
                      </td>
                      <td className="numeric-cell" style={{ paddingLeft: 13 }}>
                        {formatCurrency(obPay)}
                      </td>
                      <td className="numeric-cell" style={{ paddingLeft: 13 }}>
                        {formatCurrency(vacationCompensation)}
                      </td>
                      <td className="numeric-cell strong" style={{ paddingLeft: 13 }}>
                        {formatCurrency(totalPay)}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          )}
        </div>

        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage("")} />
      </div>

      {/* Wizard: PAXml exportflöde */}
      {showFortnoxWizard && (
        <FortnoxSetupWizard
          onClose={() => setShowFortnoxWizard(false)}
          exportPayload={exportPayload}
        />
      )}

      {showVismaWizard && (
        <VismaSetupWizard
          onClose={() => setShowVismaWizard(false)}
          exportPayload={exportPayload}
        />
      )}


      {showExportSystemSelect && (
        <ExportSystemSelectModal
          onClose={() => setShowExportSystemSelect(false)}
          onSelect={(system) => {
            setShowExportSystemSelect(false);
            setSelectedExportSystem(system);

            if (system === "fortnox") {
              setShowFortnoxWizard(true);
            }

            if (system === "visma") {
              setShowVismaWizard(true);
            }
          }}
        />
      )}

    </div>
  );
};

export default ExportPay;
